import { getCookie } from 'config/utils'
import { MAIN_EXPERIMENT } from 'middleware'

export interface IVariant {
	exclude?: string[]
	weight: number
	id: number
}

export interface IABTest {
	id: string
	name: string
	key: string
	active: boolean
	client_only: boolean
	paths: string[]
	excludedPaths?: string[]
	variants: IVariant[]
	validator?: () => boolean
}

export const COOKIE_FOR_IDS = 'ab-tests'
export const COOKIE_FOR_KEYS = 'ab-keys'

export const pathWithRegions = (path: string): string[] => {
	return [path, ...['ca', 'eu', 'uk', 'au', 'se', 'dk', 'ae', 'ch', 'mx'].map((it) => `/${it}${path}`)]
}

/**
 * Be sure to add the / at the end of the path or else things will get "mega-borked"
 */
// Commenting for template :), this test is no longer active, feel free to delete once another test is added
// export const feb27EcapAB: IABTest = {
// 	id: 'feb27EcapAB',
// 	key: 'feb27EcapAB',
// 	name: 'Feb 27 Ecap AB Test',
// 	active: true,
// 	client_only: false,
// 	paths: [],
// 	variants: [
// 		{
// 			weight: 80,
// 			id: 0,
// 		},
// 		{
// 			weight: 20,
// 			id: 1,
// 		},
// 	],
// }

export const abtests: IABTest[] = []

export const getActiveExperiments = (): IABTest[] => {
	return abtests.filter((it) => it.active)
}

export const getActiveExperimentByID = (id: string): IABTest | undefined => {
	return abtests.find((it) => it.active && it.id === id)
}

export const getActiveExperimentByKey = (key: string): IABTest | undefined => {
	return abtests.find((it) => it.active && it.key === key)
}

// export const chooseSubscriptionIncreaseABVariant = (isCalifornia: boolean, isNewYork: boolean) => {
// 	if (isCalifornia) {
// 		return subscriptionIncreaseAB.variants[1]
// 	} else if (isNewYork) {
// 		return subscriptionIncreaseAB.variants[2]
// 	} else {
// 		return subscriptionIncreaseAB.variants[0]
// 	}
// }

export const chooseExperimentVariant = (experiment: IABTest): IVariant => {
	// Calculate total weight across all variants (e.g., 50 + 50 = 100)
	const totalWeight = experiment.variants.reduce((sum, v) => sum + v.weight, 0)

	// Generate random value between 0 and total weight (e.g., 75 in a 100-weight system)
	let randomVal = Math.random() * totalWeight

	// Subtract each variant's weight from our random value until we go <= 0
	// For example, with weights [50, 50] and random value 75:
	// First iteration: 75 - 50 = 25 (continue)
	// Second iteration: 25 - 50 = -25 (return this variant)
	for (const variant of experiment.variants) {
		randomVal -= variant.weight
		if (randomVal <= 0) {
			return variant
		}
	}

	// Fallback to control (should rarely happen due to math above)
	return experiment.variants[0]
}

export const isABTestActiveVariant = (params: { experiments?: string[] | string }, experiment: IABTest, variantId: number, abTestIdsCookie?: string): boolean => {
	if (typeof window !== 'undefined') {
		if (window.sessionStorage.getItem('automated') === 'true' || window.location.search.includes('automated=true')) {
			return false
		}
	}

	if (experiment.active) {
		if (typeof window === 'undefined') {
			// condition (path.includes('/root') && variantId === 0) is for cases when checking if an A version of the test is active

			const paramsHasExperiment = params.experiments?.includes(`${experiment.id}.${variantId}`) ?? false
			const root = params.experiments === 'root' && variantId === 0
			const cookieHasExperiment = abTestIdsCookie?.includes(`${experiment.id}.${variantId}`) ?? false

			if (experiment.client_only) {
				return false
			}

			return paramsHasExperiment || root || cookieHasExperiment
		}

		const abcookie = getCookie(COOKIE_FOR_IDS)
		const expvar = `${experiment.id}.${variantId}`
		const cookieContainsAb = abcookie.includes(expvar)
		const expContainsPath = experiment.paths.includes(window.location.pathname.split('?')[0])
		const experimentExcluded = experiment.excludedPaths?.includes(window.location.pathname.split('?')[0]) ?? false
		return (experiment.paths.length === 0 || expContainsPath) && cookieContainsAb && !experimentExcluded
	}
	return false
}

export const generatePathPermutationsForExperiments = (path: string): string[] => {
	const normalized_path = (path.startsWith('/') ? path : `/${path}`) + (path.endsWith('/') || path.length === 0 ? '' : '/') // make sure the path starts and ends with /
	const activeExperiments = abtests.filter((it) => {
		if (!it.active) return false
		if (it.client_only) return false
		if (it.paths.length > 0 && !it.paths.includes(normalized_path)) return false
		if (it.excludedPaths?.includes(normalized_path) ?? false) return false
		return true
	})

	const experimentPaths: string[] = []
	activeExperiments.forEach((it) => {
		const variants = it.variants.filter((v) => v.id !== 0)
		experimentPaths.push(...variants.map((v) => `${it.id}.${v.id}`))
	})
	experimentPaths.sort((a, b) => a.length - b.length || a.localeCompare(b))

	const results: string[] = [...experimentPaths]
	experimentPaths.forEach((it, i) => {
		const prefix = it.split('.')[0]
		const rest = results.slice(i + 1).filter((el) => !el.includes(prefix))
		rest.sort((a, b) => a.length - b.length || a.localeCompare(b))

		let acc = it
		rest.forEach((p) => {
			const tmp = [it, ...p.split(';')].sort((a, b) => a.length - b.length || a.localeCompare(b))
			const newItem = tmp.join(';')
			if (!results.includes(newItem)) {
				results.push(newItem)
			}

			const prefix2 = p.split('.')[0]
			if (!acc.includes(prefix2)) {
				const tmp2 = [...acc.split(';'), ...p.split(';')].sort((a, b) => a.length - b.length || a.localeCompare(b))
				acc += tmp2.join(';')
			}
			if (!results.includes(acc) && !acc.includes(prefix2)) results.push(acc)
		})
	})

	results.sort((a, b) => a.length - b.length || a.localeCompare(b))
	return [MAIN_EXPERIMENT, ...results]
}

export function getCookieABTests(): { ids: string[]; keys: string[] } {
	const abTestIdsCookie = getCookie(COOKIE_FOR_IDS)
	const abKeysIdsCookie = getCookie(COOKIE_FOR_KEYS)
	if (!abTestIdsCookie) {
		return { ids: [], keys: [] }
	}
	const ids = abTestIdsCookie.split('%3B').filter((it) => {
		const values = it.split('.')
		const id = values[0]
		const variant = values[1]
		const exp = getActiveExperimentByID(id)
		if (exp?.validator && variant !== '0') {
			return exp.validator()
		}
		return !!exp
	})
	const keys = abKeysIdsCookie.split('%3B').filter((it) => {
		const values = it.split('_')
		const key = values[0]
		const variant = values[1]
		const exp = getActiveExperimentByKey(key)
		if (exp?.validator && variant !== '0') {
			return exp.validator()
		}
		return !!exp
	})
	return { ids, keys }
}
